import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { isAfter, parseISO } from 'date-fns'
import * as R from 'ramda'
import { getLastUpdate } from '../datatime'
import {
  deleteCargo,
  deleteCargoComment,
  getCargoUpdates,
  getCargoes,
  postCargo,
  postCopyCargo,
  putCargo,
  putCargoComment,
  putCargoConfirmation,
  sendCargo
} from '../queries'
import { alertsSlice } from './alerts'

export const cargoesSelector = state => state.cargoesSlice

const DEFAULT_CARGO_PAGE_SIZE = 5000
const MAX_CARGO_PAGES = 1000
const STREAM_PAGE_SIZE = Math.min(
  5000,
  Math.max(250, Number(import.meta.env.VITE_CARGOES_PAGE_SIZE || 1500))
)
const CARGO_PERF_ENABLED = import.meta.env.VITE_CARGO_PERF === '1'
const CARGO_PERF_LOG_THRESHOLD_MS = 16
const CARGO_INSERT_DEBUG =
  import.meta.env.DEV || import.meta.env.VITE_CARGO_INSERT_DEBUG === '1'

const logCargoPerf = (label, durationMs, meta = {}) => {
  if (!CARGO_PERF_ENABLED || durationMs < CARGO_PERF_LOG_THRESHOLD_MS) {
    return
  }

  if (typeof console !== 'undefined' && typeof console.debug === 'function') {
    console.debug(`[CargoesPerf] ${label}`, {
      durationMs: Number(durationMs.toFixed(1)),
      ...meta
    })
  }
}

const logCargoInsertDebug = (label, state, rowId) => {
  if (!CARGO_INSERT_DEBUG) {
    return
  }

  const rowKey = rowId == null ? null : String(rowId)
  const index = rowKey ? state.rowOrder.indexOf(rowKey) : -1

  if (typeof console !== 'undefined' && typeof console.debug === 'function') {
    console.debug(`[CargoInsertDebug] ${label}`, {
      rowId,
      rowKey,
      index,
      totalRows: state.rowOrder.length,
      topKeys: state.rowOrder.slice(0, 5)
    })
  }
}

const normalizeCargoItems = payload => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.result)) return payload.result
  if (Array.isArray(payload?.Result)) return payload.Result
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.Items)) return payload.Items
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.Data)) return payload.Data
  if (Array.isArray(payload?.rows)) return payload.rows
  if (Array.isArray(payload?.Rows)) return payload.Rows
  if (Array.isArray(payload?.values)) return payload.values
  if (Array.isArray(payload?.Value)) return payload.Value
  if (Array.isArray(payload?.results)) return payload.results
  return []
}

const getCargoTotalCount = (payload, pageItems) => {
  if (Array.isArray(payload)) return pageItems.length
  const total = Number(
    payload?.totalCount ??
      payload?.TotalCount ??
      payload?.count ??
      payload?.Count ??
      payload?.total ??
      payload?.Total ??
      payload?.recordsCount ??
      payload?.RecordsCount ??
      pageItems.length
  )
  return Number.isFinite(total) ? total : pageItems.length
}

const fetchCargoPages = async ({
  lastUpdate = null,
  getAll = false,
  pageSize = STREAM_PAGE_SIZE,
  onPage = () => {},
  requestId = 'n/a',
  signal
}) => {
  let page = 1
  let totalItems = 0

  while (page <= MAX_CARGO_PAGES) {
    if (signal?.aborted) {
      throw new DOMException('Cargoes load aborted', 'AbortError')
    }
    const { data } = await getCargoes({
      lastUpdate,
      getAll,
      page,
      pageSize,
      signal
    })

    if (signal?.aborted) {
      throw new DOMException('Cargoes load aborted', 'AbortError')
    }

    const pageItems = normalizeCargoItems(data)
    const totalCount = getCargoTotalCount(data, pageItems)

    if (pageItems.length > 0) {
      if (signal?.aborted) {
        throw new DOMException('Cargoes load aborted', 'AbortError')
      }
      totalItems += pageItems.length
      await onPage({ items: pageItems, totalCount })
      // Yield control so the browser can paint between streamed page inserts.
      await new Promise(resolve => window.setTimeout(resolve, 0))
    }

    if (Array.isArray(data)) {
      break
    }

    const reachedEnd = page * pageSize >= totalCount || pageItems.length === 0
    if (reachedEnd) {
      break
    }

    page += 1
  }

  return totalItems
}

const upsertCargoRow = (
  state,
  row,
  {
    insertAtStart = false,
    preserveNewity = true,
    preserveForceBottom = true,
    moveToEnd = false
  } = {}
) => {
  if (!row || row.id == null) {
    return
  }

  if (!state.data) {
    state.data = {}
  }
  if (!Array.isArray(state.rowOrder)) {
    state.rowOrder = []
  }
  if (!state.rowIndexByKey || typeof state.rowIndexByKey !== 'object') {
    state.rowIndexByKey = {}
  }

  const rowKey = String(row.id)
  const mappedIndex = state.rowIndexByKey[rowKey]
  const hasMappedIndex =
    Number.isInteger(mappedIndex) &&
    mappedIndex >= 0 &&
    state.rowOrder[mappedIndex] === rowKey
  const isExistingRow = hasMappedIndex || Object.prototype.hasOwnProperty.call(state.data, rowKey)
  const previousRow = isExistingRow ? state.data[rowKey] : null
  const nextRow =
    preserveNewity &&
    isExistingRow &&
    previousRow &&
    previousRow._newity &&
    !row._newity
      ? {
          ...row,
          _newity: previousRow._newity,
          ...(preserveForceBottom && previousRow._forceBottom && !row._forceBottom
            ? { _forceBottom: true }
            : {})
        }
      : preserveForceBottom &&
          isExistingRow &&
          previousRow &&
          previousRow._forceBottom &&
          !row._forceBottom
      ? { ...row, _forceBottom: true }
      : row

  state.data[rowKey] = nextRow
  if (!isExistingRow) {
    if (insertAtStart) {
      state.rowOrder.unshift(rowKey)
      for (const key in state.rowIndexByKey) {
        state.rowIndexByKey[key] += 1
      }
      state.rowIndexByKey[rowKey] = 0
    } else {
      state.rowOrder.push(rowKey)
      state.rowIndexByKey[rowKey] = state.rowOrder.length - 1
    }
  } else if (moveToEnd && Array.isArray(state.rowOrder)) {
    const currentIndex = hasMappedIndex
      ? mappedIndex
      : state.rowOrder.indexOf(rowKey)
    if (currentIndex >= 0) {
      state.rowOrder.splice(currentIndex, 1)
      for (let index = currentIndex; index < state.rowOrder.length; index += 1) {
        const keyAtIndex = state.rowOrder[index]
        state.rowIndexByKey[keyAtIndex] = index
      }
    }
    state.rowOrder.push(rowKey)
    state.rowIndexByKey[rowKey] = state.rowOrder.length - 1
  }

}

const removeCargoRow = (state, rowId) => {
  if (rowId == null) {
    return
  }

  const rowKey = String(rowId)
  if (state.data && Object.prototype.hasOwnProperty.call(state.data, rowKey)) {
    delete state.data[rowKey]
  }

  if (Array.isArray(state.rowOrder)) {
    const mappedIndex = state.rowIndexByKey?.[rowKey]
    const currentIndex =
      Number.isInteger(mappedIndex) &&
      mappedIndex >= 0 &&
      state.rowOrder[mappedIndex] === rowKey
        ? mappedIndex
        : state.rowOrder.indexOf(rowKey)
    if (currentIndex >= 0) {
      state.rowOrder.splice(currentIndex, 1)
      if (state.rowIndexByKey) {
        delete state.rowIndexByKey[rowKey]
        for (let index = currentIndex; index < state.rowOrder.length; index += 1) {
          const keyAtIndex = state.rowOrder[index]
          state.rowIndexByKey[keyAtIndex] = index
        }
      }
    }
  }
}

export const fetchCargoes = createAsyncThunk(
  'cargoes/fetchCargoes',
  async ({ getAll }, { dispatch, requestId, signal }) => {
    dispatch(
      cargoesSlice.actions.startPagedLoad({
        showProgress: getAll,
        requestId
      })
    )
    const totalItems = await fetchCargoPages({
      lastUpdate: null,
      getAll,
      onPage: payload =>
        dispatch(cargoesSlice.actions.upsertMany({ ...payload, requestId })),
      requestId,
      signal
    })
    return { totalItems }
  }
)

export const fetchCargoUpdates = createAsyncThunk(
  'cargoes/fetchCargoUpdates',
  async (_, { getState }) => {
    let { lastUpdate } = cargoesSelector(getState())
    if (!lastUpdate) {
      return null
    }

    let { data: remoteLastUpdate } = await getCargoUpdates()
    if (isAfter(parseISO(remoteLastUpdate), parseISO(lastUpdate))) {
      const items = []
      await fetchCargoPages({
        lastUpdate,
        getAll: false,
        onPage: ({ items: pageItems = [] }) => {
          items.push(...pageItems)
        }
      })
      return items
    }
  }
)

export const createCargo = createAsyncThunk(
  'cargoes/createCargo',
  async input => {
    const { data } = await postCargo({ data: input })
    return data
  }
)

export const editCargo = createAsyncThunk(
  'cargoes/editCargo',
  async (input, { dispatch, getState }) => {
    dispatch(cargoesSlice.actions.set(input))

    const { data } = await putCargo({ data: input })
    const updatedRow = data?.[0]
    if (updatedRow?.id == null) {
      return data
    }

    const cargoesState = cargoesSelector(getState())
    const alreadyExists = Boolean(cargoesState?.data?.[String(updatedRow.id)])
    const idChanged = input?.id != null && updatedRow.id !== input.id
    const shouldInsertBottom = idChanged || !alreadyExists

    if (shouldInsertBottom) {
      dispatch(cargoesSlice.actions.setBottom(updatedRow))
    } else {
      dispatch(cargoesSlice.actions.set(updatedRow))
    }
    return data
  }
)

export const copyCargo = createAsyncThunk(
  'cargoes/copyCargo',
  async (input, { dispatch }) => {
    const { data } = await postCopyCargo({ data: input })
    return data
  }
)

export const removeCargo = createAsyncThunk(
  'cargoes/removeCargo',
  async (input, { dispatch }) => {
    dispatch(cargoesSlice.actions.delete(input))
    const { data } = await deleteCargo({ id: input.id })
    dispatch(
      alertsSlice.actions.push({
        color: 'success',
        message: 'Your cargo has been deleted successfully.'
      })
    )
    return data
  }
)

export const sendCargoStatus = createAsyncThunk(
  'cargoes/sendCargoStatus',
  async ({ row, onlySave = false }) => {
    const { data } = await sendCargo({ data: row, onlySave })
    return data
  }
)

export const confirmCargo = createAsyncThunk(
  'cargoes/confirmCargo',
  async ({ cargo, confirmations, comments, _delete }, { dispatch }) => {
    try {
      await Promise.all([
        ...Object.values(comments).map(data => putCargoComment({ data })),
        ...(_delete?.comments || []).map(data =>
          deleteCargoComment({ id: data.id })
        )
      ])
      const { data } = await putCargoConfirmation({
        cargoId: cargo.id,
        chartererId: cargo.chartererId,
        data: confirmations
      })
      return data
    } catch (err) {
      dispatch(
        alertsSlice.actions.push({
          color: 'danger',
          message: 'An error occurred while confirming this cargo.'
        })
      )
    }
  }
)

const initialState = {
  data: null,
  rowOrder: [],
  rowIndexByKey: {},
  status: 'idle',
  error: null,
  lastUpdate: null,
  lastInsertedCargoId: null,
  isCreating: false,
  currentRequestId: null,
  loadProgress: {
    active: false,
    show: false,
    loaded: 0,
    total: null
  }
}

export const cargoesSlice = createSlice({
  name: 'cargoes',
  initialState,
  reducers: {
    startPagedLoad: (state, action) => {
      const showProgress = Boolean(action.payload?.showProgress)
      state.currentRequestId = action.payload?.requestId || null
      state.data = {}
      state.rowOrder = []
      state.rowIndexByKey = {}
      state.error = null
      state.status = 'loading'
      state.loadProgress = {
        active: true,
        show: showProgress,
        loaded: 0,
        total: null
      }
    },
    upsertMany: (state, action) => {
      const startedAt = CARGO_PERF_ENABLED ? performance.now() : 0
      if (
        action.payload?.requestId &&
        state.currentRequestId &&
        action.payload.requestId !== state.currentRequestId
      ) {
        return
      }

      const rows = action.payload?.items || []
      const totalCount = Number(action.payload?.totalCount)
      let added = 0
      rows.forEach(row => {
        const rowKey = row?.id == null ? null : String(row.id)
        if (!rowKey || !Object.prototype.hasOwnProperty.call(state.data, rowKey)) {
          added += 1
        }
        upsertCargoRow(state, row)
      })
      if (state.loadProgress.active) {
        state.loadProgress.loaded += added
        if (!Number.isNaN(totalCount) && totalCount >= 0) {
          state.loadProgress.total = totalCount
        }
      }
      // Allow UI table rendering while additional pages are still loading.
      state.status = 'success'

      if (CARGO_PERF_ENABLED) {
        logCargoPerf('upsertMany', performance.now() - startedAt, {
          incomingRows: rows.length,
          addedRows: added,
          totalRows: state.rowOrder?.length || 0
        })
      }
    },
    set: (state, action) => {
      upsertCargoRow(state, action.payload)
    },
    setBottom: (state, action) => {
      if (!action.payload) return
      const row = { ...action.payload }
      delete row._newity
      row._forceBottom = true

      const rowKey = String(row.id)
      upsertCargoRow(state, row, {
        insertAtStart: false,
        preserveNewity: false,
        preserveForceBottom: true,
        moveToEnd: true
      })

    },
    delete: (state, action) => {
      removeCargoRow(state, action.payload?.id)
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCargoes.pending, (state) => {
      state.status = 'loading'
    })
    builder.addCase(fetchCargoes.rejected, (state, action) => {
      if (action.meta.requestId !== state.currentRequestId) {
        return
      }

      if (action.error?.name === 'AbortError') {
        state.status = 'idle'
        state.error = null
      } else {
        state.status = 'error'
        state.error = action.payload
      }
      state.loadProgress.active = false
      state.currentRequestId = null
    })
    builder.addCase(fetchCargoes.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.currentRequestId) {
        return
      }

      state.status = 'success'
      state.loadProgress.active = false
      state.lastUpdate = getLastUpdate()
      state.currentRequestId = null
    })
    builder.addCase(fetchCargoUpdates.fulfilled, (state, action) => {
      const startedAt = CARGO_PERF_ENABLED ? performance.now() : 0
      if (action.payload) {
        const rows = action.payload
        const stateData = state.data
        for (let index = 0; index < rows.length; index += 1) {
          const row = rows[index]
          const rowId = row?.id
          const rowKey = rowId == null ? null : String(rowId)
          const isNewRow = rowKey != null && !stateData?.[rowKey]
          if (isNewRow) {
            const nextRow = { ...row, _forceBottom: true }
            delete nextRow._newity
            upsertCargoRow(state, nextRow, {
              insertAtStart: false,
              preserveNewity: false,
              preserveForceBottom: true,
              moveToEnd: true
            })
          } else {
            upsertCargoRow(state, row)
          }
        }
        state.lastUpdate = getLastUpdate()

        if (CARGO_PERF_ENABLED) {
          logCargoPerf('mergeUpdates', performance.now() - startedAt, {
            incomingRows: rows.length,
            totalRows: state.rowOrder?.length || 0
          })
        }
      }
    })
    builder.addCase(createCargo.pending, (state) => {
      state.isCreating = true
    })
    builder.addCase(createCargo.fulfilled, (state, action) => {
      upsertCargoRow(state, {
        ...action.payload,
        _newity: new Date().getTime()
      }, { insertAtStart: true })
      state.isCreating = false
      state.lastInsertedCargoId = action.payload?.id ?? null
      state.lastUpdate = getLastUpdate()
      logCargoInsertDebug('createCargo.fulfilled', state, action.payload?.id)
    })
    builder.addCase(copyCargo.pending, (state) => {
      state.isCreating = true
    })
    builder.addCase(copyCargo.fulfilled, (state, action) => {
      upsertCargoRow(state, {
        ...action.payload,
        _newity: new Date().getTime()
      }, { insertAtStart: true })
      state.isCreating = false
      state.lastInsertedCargoId = action.payload?.id ?? null
      state.lastUpdate = getLastUpdate()
      logCargoInsertDebug('copyCargo.fulfilled', state, action.payload?.id)
    })
    builder.addCase(sendCargoStatus.fulfilled, (state, action) => {
      upsertCargoRow(state, action.payload)
      state.lastUpdate = getLastUpdate()
    })
    builder.addCase(confirmCargo.fulfilled, (state, action) => {
      if (!Array.isArray(action.payload) || action.payload.length === 0) {
        return
      }

      const allConfirmed =
        action.payload.length &&
        action.payload.every(({ confirmed }) => confirmed)
      const oldData = state.data[action.payload[0].cargoId]

      if (!oldData) {
        return
      }

      if (oldData.chartererConfirmed !== allConfirmed) {
        upsertCargoRow(state, {
          ...oldData,
          chartererConfirmed: allConfirmed
        })
        state.lastUpdate = getLastUpdate()
      }
    })
  }
})
